import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Typography, Row, Col, Divider, Space, Skeleton, Grid, message } from 'antd';
import { PrinterOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import leadPurchasesService from '../../services/leadPurchases';
import { useReactToPrint } from 'react-to-print';
import PurchaseVerificationPrint from '../../components/PurchaseVerificationPrint';

const { useBreakpoint } = Grid;
const { Title, Text } = Typography;

const InvoicePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const printRef = React.useRef();

  const { data: purchase, isLoading, error } = useQuery({
    queryKey: ['purchase', id],
    queryFn: () => leadPurchasesService.getPurchase(id),
    retry: false
  });

  useEffect(() => {
    if (error?.response?.status === 403) {
      //message.error('You are not authorized to view this invoice');
      navigate('/purchases');
    } else if (error?.response?.status === 404) {
      //message.error('Invoice not found');
      navigate('/purchases');
    }
  }, [error, navigate]);

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div style={{ padding: screens.xs ? '16px' : '24px' }}>
        {/* Header Skeleton */}
        <div className="invoice-header-gradient" style={{
          marginBottom: screens.xs ? '16px' : '32px',
          padding: screens.xs ? '24px 16px' : '32px 24px',
          background: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
          borderRadius: '16px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: screens.xs ? 'column' : 'row',
          alignItems: screens.xs ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          gap: screens.xs ? '16px' : '0'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Skeleton.Input active size="large" style={{ width: 200, height: 40 }} />
            <Skeleton.Input active size="small" style={{ width: 150, height: 24 }} />
            <Skeleton.Input active size="small" style={{ width: 180, height: 24 }} />
          </div>
          <div style={{ 
            display: 'flex', 
            gap: 16,
            flexDirection: screens.xs ? 'column' : 'row',
            width: screens.xs ? '100%' : 'auto'
          }}>
            <Skeleton.Button active size="large" style={{ width: screens.xs ? '100%' : 150, height: 44 }} />
            <Skeleton.Button active size="large" style={{ width: screens.xs ? '100%' : 150, height: 44 }} />
          </div>
        </div>

        {/* Content Skeleton */}
        <div className="invoice-content" style={{ 
          padding: screens.xs ? '24px' : '40px',
          backgroundColor: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: '8px'
        }}>
          <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12}>
              <Skeleton.Input active size="large" style={{ width: 200, height: 32, marginBottom: 16 }} />
              <Skeleton.Input active size="small" style={{ width: 150, height: 24 }} />
            </Col>
            <Col xs={24} sm={12} style={{ textAlign: screens.xs ? 'left' : 'right', marginTop: screens.xs ? '16px' : '0' }}>
              <Skeleton.Input active size="small" style={{ width: 180, height: 24 }} />
            </Col>
          </Row>

          <Divider />

          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12}>
              <Skeleton.Input active size="large" style={{ width: 150, height: 28, marginBottom: 16 }} />
              <Skeleton.Input active size="small" style={{ width: 200, height: 24, marginBottom: 8 }} />
              <Skeleton.Input active size="small" style={{ width: 250, height: 24, marginBottom: 8 }} />
              <Skeleton.Input active size="small" style={{ width: 220, height: 24 }} />
            </Col>
            <Col xs={24} sm={12}>
              <Skeleton.Input active size="large" style={{ width: 150, height: 28, marginBottom: 16 }} />
              <Skeleton.Input active size="small" style={{ width: 200, height: 24, marginBottom: 8 }} />
              <Skeleton.Input active size="small" style={{ width: 250, height: 24, marginBottom: 8 }} />
              <Skeleton.Input active size="small" style={{ width: 220, height: 24 }} />
            </Col>
          </Row>

          <Divider />

          <Skeleton.Input active size="large" style={{ width: 200, height: 28, marginBottom: 24 }} />
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12}>
              <Skeleton.Input active size="small" style={{ width: 150, height: 24, marginBottom: 8 }} />
              <Skeleton.Input active size="small" style={{ width: 200, height: 24 }} />
            </Col>
            <Col xs={24} sm={12}>
              <Skeleton.Input active size="small" style={{ width: 150, height: 24, marginBottom: 8 }} />
              <Skeleton.Input active size="small" style={{ width: 200, height: 24 }} />
            </Col>
            <Col xs={24} sm={12}>
              <Skeleton.Input active size="small" style={{ width: 150, height: 24, marginBottom: 8 }} />
              <Skeleton.Input active size="small" style={{ width: 200, height: 24 }} />
            </Col>
            <Col xs={24} sm={12}>
              <Skeleton.Input active size="small" style={{ width: 150, height: 24, marginBottom: 8 }} />
              <Skeleton.Input active size="small" style={{ width: 200, height: 24 }} />
            </Col>
          </Row>

          <Divider />

          <Row justify="end">
            <Col xs={24} sm={12}>
              <Space direction="vertical" align={screens.xs ? 'start' : 'end'} style={{ width: '100%' }}>
                <Skeleton.Input active size="small" style={{ width: 150, height: 24, marginBottom: 8 }} />
                <Skeleton.Input active size="small" style={{ width: 150, height: 24, marginBottom: 8 }} />
                <Skeleton.Input active size="large" style={{ width: 180, height: 32 }} />
              </Space>
            </Col>
          </Row>

          <Divider />

          <Skeleton.Input active size="small" style={{ width: 300, height: 24 }} />
        </div>
      </div>
    );
  }

  if (error) {
    if (error.response?.status === 403 || error.response?.status === 404) {
      return null; // Will be handled by useEffect
    }
    return (
      <div style={{ padding: screens.xs ? '16px' : '24px' }}>
        <Text type="danger">Error loading invoice: {error.message}</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: screens.xs ? '16px' : '24px' }}>
      {/* Gradient Header (screen only) */}
      <div className="invoice-header-gradient" style={{
        marginBottom: screens.xs ? '16px' : '32px',
        padding: screens.xs ? '24px 16px' : '32px 24px',
        background: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
        borderRadius: '16px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        display: 'flex',
        flexDirection: screens.xs ? 'column' : 'row',
        alignItems: screens.xs ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        gap: screens.xs ? '16px' : '0'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Title level={2} style={{ 
            color: 'white', 
            margin: 0, 
            fontWeight: 700, 
            letterSpacing: 1, 
            textShadow: '0 2px 8px rgba(0,0,0,0.12)',
            fontSize: screens.xs ? '24px' : '32px'
          }}>
            INVOICE
          </Title>
          <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: screens.xs ? '16px' : '18px' }}>
            Invoice #{purchase.id}
          </Text>
          <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: screens.xs ? '14px' : '16px' }}>
            Date: {new Date(purchase.created_at).toLocaleDateString()}
          </Text>
        </div>
        <div style={{ 
          display: 'flex', 
          gap: 16,
          flexDirection: screens.xs ? 'column' : 'row',
          width: screens.xs ? '100%' : 'auto'
        }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/purchases')}
            style={{
              background: 'rgba(255,255,255,0.12)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontWeight: 500,
              fontSize: screens.xs ? '14px' : '16px',
              height: screens.xs ? '40px' : '44px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
              width: screens.xs ? '100%' : 'auto'
            }}
          >
            Back to Purchases
          </Button>
          <Button 
            type="primary" 
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            style={{
              background: 'black',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: screens.xs ? '14px' : '16px',
              height: screens.xs ? '40px' : '44px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              width: screens.xs ? '100%' : 'auto'
            }}
          >
            Print Invoice
          </Button>
        </div>
      </div>

      <div className="invoice-content" style={{ 
        padding: screens.xs ? '24px' : '40px',
        backgroundColor: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        borderRadius: '8px'
      }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12}>
            <Title level={3} style={{ fontSize: screens.xs ? '20px' : '24px' }}>INVOICE</Title>
            <Text>Invoice #{purchase.id}</Text>
          </Col>
          <Col xs={24} sm={12} style={{ textAlign: screens.xs ? 'left' : 'right', marginTop: screens.xs ? '16px' : '0' }}>
            <Text>Date: {new Date(purchase.created_at).toLocaleDateString()}</Text>
          </Col>
        </Row>

        <Divider />

        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12}>
            <Title level={4} style={{ fontSize: screens.xs ? '16px' : '18px' }}>Bill To:</Title>
            <Text>{purchase.lead.first_name} {purchase.lead.last_name}</Text>
            <br />
            <Text>{purchase.lead.address}</Text>
            <br />
            <Text>{purchase.lead.city}, {purchase.lead.state} {purchase.lead.zip_code}</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Title level={4} style={{ fontSize: screens.xs ? '16px' : '18px' }}>From:</Title>
            <Text>Lead Management System</Text>
            <br />
            <Text>123 Business Street</Text>
            <br />
            <Text>Business City, State ZIP</Text>
          </Col>
        </Row>

        <Divider />

        <Title level={4} style={{ fontSize: screens.xs ? '16px' : '18px' }}>Lead Details</Title>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12}>
            <Text strong>Lead Name:</Text>
            <br />
            <Text>{purchase.lead.first_name} {purchase.lead.last_name}</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>Email:</Text>
            <br />
            <Text>{purchase.lead.email}</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>Phone:</Text>
            <br />
            <Text>{purchase.lead.phone}</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>Transaction ID:</Text>
            <br />
            <Text>{purchase.transaction_id}</Text>
          </Col>
        </Row>

        <Divider />

        <Row justify="end">
          <Col xs={24} sm={12}>
            <Space direction="vertical" align={screens.xs ? 'start' : 'end'} style={{ width: '100%' }}>
              <Text>Subtotal: ${purchase.amount}</Text>
              <Text>Tax (0%): $0.00</Text>
              <Title level={4} style={{ fontSize: screens.xs ? '18px' : '20px' }}>Total: ${purchase.amount}</Title>
            </Space>
          </Col>
        </Row>

        <Divider />

        <Text type="secondary" style={{ fontSize: screens.xs ? '10px' : '12px' }}>
          This is a computer-generated invoice. No signature is required.
        </Text>
      </div>

      <style>
        {`
          @media print {
            .ant-layout-header,
            .ant-layout-sider,
            .ant-layout-footer,
            button {
              display: none !important;
            }
            .invoice-content {
              box-shadow: none !important;
              padding: 0 !important;
            }
            .invoice-header-gradient {
              background: none !important;
              box-shadow: none !important;
              padding: 0 !important;
              margin-bottom: 24px !important;
            }
            .invoice-header-gradient h2,
            .invoice-header-gradient .ant-typography {
              color: #000 !important;
              text-shadow: none !important;
            }
            .invoice-header-gradient .ant-typography {
              opacity: 1 !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default InvoicePage; 